import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SaveIcon from "@mui/icons-material/Save";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import { formatDateTime } from "../../utils/hooks/dataHandler";

const DESCRIPTION_MAX_LENGTH = 500;

/* ─── Skeleton placeholder while data loads ──────────────── */

const OrganizationSkeleton = () => (
  <Container maxWidth='md'>
    {/* Header skeleton */}
    <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, pt: 1 }}>
      <Skeleton variant='circular' width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant='text' width={180} height={32} />
        <Skeleton variant='text' width={280} height={20} />
      </Box>
    </Box>

    {/* Basic Info card skeleton */}
    <Card
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Skeleton variant='text' width={140} height={28} sx={{ mb: 2.5 }} />
        <Skeleton
          variant='rounded'
          height={56}
          sx={{ mb: 2.5, borderRadius: 1 }}
        />
        <Skeleton
          variant='rounded'
          height={56}
          sx={{ mb: 2.5, borderRadius: 1 }}
        />
        <Skeleton variant='rounded' height={120} sx={{ borderRadius: 1 }} />
      </CardContent>
    </Card>

    {/* Metadata card skeleton */}
    <Card
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Skeleton variant='text' width={160} height={28} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Skeleton variant='rounded' height={48} sx={{ borderRadius: 1 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Skeleton variant='rounded' height={48} sx={{ borderRadius: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant='rounded' height={48} sx={{ borderRadius: 1 }} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  </Container>
);

/* ─── Section card wrapper ───────────────────────────────── */

const SectionCard = ({ icon, title, subtitle, children, sx }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        transition:
          "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.06)}`,
        },
        ...sx,
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          "&:last-child": { pb: { xs: 2.5, sm: 3.5 } },
        }}
      >
        {/* Card header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
              color: "primary.main",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 700, lineHeight: 1.3 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant='caption' color='text.secondary'>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {children}
      </CardContent>
    </Card>
  );
};

/* ─── Read-only metadata field ───────────────────────────── */

const MetadataField = ({ label, value, mono }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 2,
      bgcolor: "action.hover",
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography
      variant='caption'
      color='text.secondary'
      sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        mt: 0.25,
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: mono ? "break-all" : "normal",
        overflowWrap: "break-word",
        color: "text.primary",
        fontWeight: 500,
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const OptionalLabel = ({ children }) => (
  <Box
    component='span'
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
  >
    {children}
    <Typography
      component='span'
      variant='caption'
      sx={{ color: "text.disabled", fontWeight: 400, fontStyle: "italic" }}
    >
      (Optional)
    </Typography>
  </Box>
);

/* ═══════════════════════════════════════════════════════════
   OrganizationPage
   ═══════════════════════════════════════════════════════════ */

const OrganizationPage = ({ canAccess }) => {
  const theme = useTheme();

  const { getOrganization, createOrganization, updateOrganization } =
    useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payload, setPayload] = useState({
    subscription_id: null,
    organization: null,
    canManage: false,
  });
  const [form, setForm] = useState({
    name: "",
    GST_registration_no: "",
    description: "",
  });

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  /* ── data loading ─────────────────────────────────────── */

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOrganization();
      const org = data?.organization ?? null;
      setPayload({
        subscription_id: data?.subscription_id ?? null,
        organization: org,
        canManage: canAccess,
      });
      setForm({
        name: org?.name ?? "",
        GST_registration_no: org?.GST_registration_no ?? "",
        description: org?.description ?? "",
      });
    } catch (error) {
      handleError(
        error?.message ||
          error?.response?.data?.message ||
          "Failed to load organization.",
      );
    } finally {
      setLoading(false);
    }
  }, [getOrganization]);

  useEffect(() => {
    load();
  }, [load]);

  /* ── derived state ────────────────────────────────────── */

  const hasOrganization = Boolean(payload.organization?.id);
  const disabled = !payload.canManage;
  const descriptionLength = form.description.length;

  /* ── handlers ─────────────────────────────────────────── */

  const handleField = (field) => (e) => {
    const value = e.target.value;
    // Enforce max length on description
    if (field === "description" && value.length > DESCRIPTION_MAX_LENGTH)
      return;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!payload.canManage) return;
    const name = form.name.trim();
    if (!name) {
      handleError("Organization name is required.");
      return;
    }

    try {
      setSaving(true);
      if (!hasOrganization) {
        await createOrganization({
          name,
          GST_registration_no: form.GST_registration_no.trim() || null,
          description: form.description.trim() || null,
        });
        handleSuccess("Organization created.");
      } else {
        await updateOrganization({
          name,
          GST_registration_no: form.GST_registration_no.trim() || null,
          description: form.description.trim() || null,
        });
        handleSuccess("Organization updated.");
      }
      await load();
    } catch (error) {
      console.error(error);
      handleError(
        error?.message ||
          error?.response?.data?.message ||
          "Could not save organization.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* ── loading skeleton ─────────────────────────────────── */

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 70px)",
          width: "100%",
          overflow: "auto",
          bgcolor: "background.default",
          py: { xs: 2, sm: 3 },
        }}
      >
        <OrganizationSkeleton />
      </Box>
    );
  }

  /* ── render ───────────────────────────────────────────── */

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 70px)",
        width: "100%",
        overflow: "auto",
        bgcolor: "background.default",
        py: { xs: 2, sm: 3 },
        // Extra bottom padding so fixed action bar doesn't overlap content
        pb: payload.canManage ? { xs: 10, sm: 11 } : { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth='md'>
        {/* ═══ Header ═══════════════════════════════════════ */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
            px: { md: 0.5 },
          }}
        >
          <BusinessIcon color='primary' sx={{ fontSize: { xs: 32, sm: 36 } }} />

          <Box sx={{ flex: 1 }}>
            <Typography
              variant='h5'
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                lineHeight: 1.3,
              }}
            >
              Organization
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mt: 0.25 }}
            >
              Subscription workspace details for your team
            </Typography>
          </Box>

          {/* Status chip */}
          {hasOrganization && (
            <Chip
              label='Active'
              size='small'
              color='success'
              variant='outlined'
              sx={{ fontWeight: 600, display: { xs: "none", sm: "flex" } }}
            />
          )}
        </Box>

        {/* Admin notice */}
        {!payload.canManage && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
              border: "1px solid",
              borderColor: (t) => alpha(t.palette.warning.main, 0.25),
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <LockOutlinedIcon sx={{ color: "warning.main", fontSize: 20 }} />
            <Typography variant='body2' color='text.secondary'>
              Only an admin account with the admin role can create or change
              organization details.
            </Typography>
          </Box>
        )}

        {/* ═══ Card 1 — Basic Information ═══════════════════ */}
        <SectionCard
          icon={<InfoOutlinedIcon fontSize='small' />}
          title='Basic Information'
          subtitle='Core details about your organization'
        >
          <Grid container spacing={2.5}>
            {/* Organization name */}
            <Grid item xs={12} md={6}>
              <TextField
                label='Organization Name'
                value={form.name}
                onChange={handleField("name")}
                fullWidth
                required
                disabled={disabled}
                placeholder='Enter your organization name'
                variant='outlined'
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* GST */}
            <Grid item xs={12} md={6}>
              <TextField
                label={<OptionalLabel>GST registration number</OptionalLabel>}
                value={form.GST_registration_no}
                onChange={handleField("GST_registration_no")}
                fullWidth
                disabled={disabled}
                placeholder='e.g. 12-3456789-0'
                variant='outlined'
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label={<OptionalLabel>Description</OptionalLabel>}
                value={form.description}
                onChange={handleField("description")}
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                disabled={disabled}
                placeholder='Tell us a bit about your organization…'
                variant='outlined'
                InputLabelProps={{ shrink: true }}
                helperText={
                  <Box
                    component='span'
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 0.25,
                    }}
                  >
                    <Typography
                      component='span'
                      variant='caption'
                      sx={{
                        color:
                          descriptionLength > DESCRIPTION_MAX_LENGTH * 0.9
                            ? "warning.main"
                            : "text.disabled",
                        fontWeight:
                          descriptionLength > DESCRIPTION_MAX_LENGTH * 0.9
                            ? 600
                            : 400,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {descriptionLength}/{DESCRIPTION_MAX_LENGTH}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Save Button - Right aligned */}
            {payload.canManage && (
              <Grid item xs={12} display='flex' justifyContent='flex-end'>
                <Button
                  variant='contained'
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color='inherit' />
                    ) : hasOrganization ? (
                      <SaveIcon fontSize='small' />
                    ) : (
                      <AddBusinessIcon fontSize='small' />
                    )
                  }
                  sx={{
                    minWidth: 180,
                    height: 44,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    boxShadow: saving ? "none" : undefined,
                  }}
                >
                  {saving
                    ? "Saving…"
                    : hasOrganization
                      ? "Save changes"
                      : "Create organization"}
                </Button>
              </Grid>
            )}
          </Grid>
        </SectionCard>

        {/* ═══ Card 2 — System Metadata ═════════════════════ */}
        {hasOrganization && (
          <SectionCard
            icon={<ScheduleIcon fontSize='small' />}
            title='Organization Meta Info'
            subtitle='Here is some additional information about your organization.'
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MetadataField
                  label='Created'
                  value={formatDateTime(payload.organization?.created_at)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MetadataField
                  label='Last updated'
                  value={formatDateTime(payload.organization?.updated_at)}
                />
              </Grid>
            </Grid>
          </SectionCard>
        )}
      </Container>
    </Box>
  );
};

export default OrganizationPage;
